import type { FlowState } from '@de/providers/dynamicExperienceReducer';

/**
 * A valid ISO 3166 country or subdivision segment: letters and digits
 * only, after trim + upper-case. Spaces and extra hyphens are invalid
 * (e.g. `GREATER LONDON`, `ILE-DE-FRANCE`) and must not be emitted.
 */
const VALID_SEGMENT = /^[A-Za-z0-9]+$/;

function toValidSegment(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().toUpperCase();
  return VALID_SEGMENT.test(normalized) ? normalized : undefined;
}

/**
 * Resolve the viewer's geolocation as `CC` or `CC-SUB` (availability's
 * accepted shapes) from the global `flowState.location` slot, suitable
 * for binding into a DSL offerings data source as
 * `{{formState.geolocation}}`.
 *
 * The `location` slot is the single merge point for both location sources:
 *   - **User-answered location question** — when a ZIP question is answered,
 *     {@link useHandleStepForward} validates it and overwrites
 *     `location.state` with the resolved `stateAbbr`.
 *   - **IP inference** — {@link initializeAutoPopulation} seeds
 *     `location.state` / `location.countryCode` from the IP lookup.
 * Because the question answer overwrites the IP seed in the same slot, the
 * question value naturally wins and the IP value is the default — no extra
 * precedence logic is needed here.
 *
 * Emit rules:
 *   - Country + valid region → `CC-SUB` (e.g. `US-VA`, `CA-ON`, `GB-ENG`)
 *   - Country, no region or an invalid region → country-only (`GB`, `FR`, `US`)
 *   - Valid region, no usable country → default `US-<region>`
 *   - No usable country and no valid region → `undefined`
 *
 * Country-only is a valid bind value. `undefined` means no usable country
 * yet (no location question and no IP country), not "no region". Callers
 * bind the result as a pure token so an `undefined` is dropped from the
 * request body entirely.
 */
export function resolveGeolocation(
  flowState: FlowState | undefined,
): string | undefined {
  const location = flowState?.['location'] as
    | { state?: unknown; countryCode?: unknown }
    | undefined;

  const region = toValidSegment(location?.state);
  const country = toValidSegment(location?.countryCode);

  if (country && region) return `${country}-${region}`;
  if (country) return country;
  if (region) return `US-${region}`;
  return undefined;
}
